<!--
  Visual hierarchy (AGENTS.md §Visual hierarchy)
  Question: "Does this receipt's split add up, and where is each person's share going?"
  Answer:   the remaining-to-allocate figure - the one number that has to hit zero before
            this can post, shown live as lines are typed, and the amount column itself.
  Context:  the defaults bar (date, payee, total, and the fixed payment account - myWealth
            §4.3, no per-transaction picker) set once at the top, never retyped per line;
            the set-all row underneath it.
  Controls: "Split evenly" and "Apply to all" are the set-all shortcuts (myWealth's
            defaults-bar pattern); "Split receipt" is the one --color-action control.
-->
<script setup lang="ts">
import MoneyAmount from '@/app/components/MoneyAmount.vue'
import { ActionButton, StatusTag } from '@taler/ui'
import { formatLocalDate, type Money } from '@taler/domain'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import AmountInput from '@/app/components/AmountInput.vue'
import { rowCurrency, type RowDraft } from '../internal/draft.ts'
import type { Frecency, Target } from '../internal/targets.ts'
import AccountCombobox from '@/features/entries/components/AccountCombobox.vue'

const props = defineProps<{
  row: RowDraft
  payee: string
  total: Money | null
  paymentAccountName: string | undefined
  counterTargets: readonly Target[]
  categoryTargets: readonly Target[]
  targetsById: ReadonlyMap<string, Target>
  frecency: ReadonlyMap<string, Frecency>
  remaining: Money | undefined
  canSubmit: boolean
  isSubmitting: boolean
  hasSubmitError: boolean
  submitErrorDetail: string | undefined
  justPosted: boolean
}>()

const emit = defineEmits<{
  'update:payee': [string]
  'update:total': [Money | null]
  'update:date': [string]
  setTarget: [splitId: string, targetId: string | undefined]
  addLine: []
  removeLine: [splitId: string]
  splitEvenly: []
  applyCategoryToAll: [categoryTargetId: string]
  submit: []
}>()

const { t, locale } = useI18n()
const currentLocale = computed(() => locale.value as 'en' | 'hu')
const currency = computed(() => rowCurrency(props.row, props.targetsById))

const setAllCategoryId = defineModel<string | undefined>('setAllCategoryId')

function onDateChange(value: string): void {
  if (value === '') return
  emit('update:date', value)
}

const remainingParts = computed(() => (props.remaining === undefined ? undefined : props.remaining))

function isContactTarget(targetId: string | undefined): boolean {
  return targetId !== undefined && props.targetsById.get(targetId)?.source === 'contact'
}

function applyCategoryToAll(): void {
  if (setAllCategoryId.value === undefined) return
  emit('applyCategoryToAll', setAllCategoryId.value)
}
</script>

<template>
  <div class="flex flex-col gap-[var(--space-block)]">
    <div
      class="grid grid-cols-1 gap-[var(--space-block)] rounded-[var(--radius-surface)] border border-[color:var(--color-border)] bg-[color:var(--color-surface-raised)] p-[var(--space-block)] sm:grid-cols-4"
    >
      <label class="flex flex-col gap-[var(--space-hair)]">
        <span class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">{{
          t('splitExpense.date')
        }}</span>
        <input
          type="date"
          :value="formatLocalDate(row.date)"
          data-field="date"
          class="min-h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-inline)] text-[length:var(--text-step-3)] text-[color:var(--color-text)]"
          @change="(event) => onDateChange((event.target as HTMLInputElement).value)"
        />
      </label>
      <div class="flex flex-col gap-[var(--space-hair)]" data-field="payment-account">
        <span class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">{{
          t('splitExpense.paidFrom')
        }}</span>
        <p class="flex min-h-[var(--density-control)] items-center gap-[var(--space-tight)]">
          <span class="text-[length:var(--text-step-3)] text-[color:var(--color-text)]">{{
            paymentAccountName
          }}</span>
          <RouterLink
            :to="{ name: 'accounts' }"
            class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)] underline"
          >
            {{ t('paymentAccount.change') }}
          </RouterLink>
        </p>
      </div>
      <label class="flex flex-col gap-[var(--space-hair)]">
        <span class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">{{
          t('splitExpense.payee')
        }}</span>
        <input
          :value="payee"
          type="text"
          maxlength="100"
          data-field="payee"
          class="min-h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-inline)] text-[length:var(--text-step-3)] text-[color:var(--color-text)]"
          @input="emit('update:payee', ($event.target as HTMLInputElement).value)"
        />
      </label>
      <label class="flex flex-col gap-[var(--space-hair)]">
        <span class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">{{
          t('splitExpense.total')
        }}</span>
        <AmountInput
          :model-value="total"
          :currency="currency"
          :locale="currentLocale"
          data-field="total"
          @update:model-value="(value) => emit('update:total', value)"
        />
      </label>
    </div>

    <div
      class="flex flex-wrap items-center justify-between gap-[var(--space-block)] rounded-[var(--radius-surface)] border border-[color:var(--color-border)] p-[var(--space-block)] bg-[color:var(--color-surface)]"
    >
      <div class="flex flex-col gap-[var(--space-hair)]">
        <span
          class="text-[length:var(--text-step-1)] font-semibold uppercase tracking-[var(--tracking-caps)] text-[color:var(--color-text-muted)]"
        >
          {{ t('splitExpense.remaining') }}
        </span>
        <MoneyAmount :value="remainingParts" v-if="remainingParts" />
        <span v-else class="text-[length:var(--text-step-3)] text-[color:var(--color-text-muted)]">—</span>
        <StatusTag v-if="remaining !== undefined && remaining.units !== 0n" tone="danger">
          {{ remaining.units > 0n ? t('splitExpense.remainingShort') : t('splitExpense.remainingOver') }}
        </StatusTag>
      </div>

      <div class="flex flex-wrap items-end gap-[var(--space-group)]">
        <button
          type="button"
          class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-[var(--space-cell)] text-[length:var(--text-step-2)] text-[color:var(--color-text)]"
          @click="emit('splitEvenly')"
        >
          {{ t('splitExpense.splitEvenly') }}
        </button>
        <label class="flex flex-col gap-[var(--space-hair)]">
          <span class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">{{
            t('splitExpense.setAllCategory')
          }}</span>
          <div data-field="set-all-category">
            <AccountCombobox
              id="split-expense-set-all-category"
              :label="t('splitExpense.setAllCategory')"
              :model-value="setAllCategoryId"
              :targets="categoryTargets"
              :frecency="frecency"
              @update:model-value="(value) => (setAllCategoryId = value)"
            />
          </div>
        </label>
        <button
          type="button"
          :disabled="setAllCategoryId === undefined"
          class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-[var(--space-cell)] text-[length:var(--text-step-2)] text-[color:var(--color-text)] disabled:opacity-60"
          @click="applyCategoryToAll"
        >
          {{ t('splitExpense.applyToAll') }}
        </button>
      </div>
    </div>

    <div
      class="overflow-x-auto rounded-[var(--radius-surface)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)]"
    >
      <table class="w-full min-w-[44rem] border-collapse">
        <thead>
          <tr
            class="border-b border-[color:var(--color-border)] bg-[color:var(--color-surface-raised)] text-left text-[length:var(--text-step-1)] font-semibold uppercase tracking-[var(--tracking-caps)] text-[color:var(--color-text-muted)]"
          >
            <th class="px-[var(--space-cell)] py-[var(--space-inline)]" scope="col">
              {{ t('splitExpense.targetColumn') }}
            </th>
            <th class="px-[var(--space-cell)] py-[var(--space-inline)] text-right" scope="col">
              {{ t('entries.amount') }}
            </th>
            <th class="px-[var(--space-cell)] py-[var(--space-inline)]" scope="col">
              {{ t('entries.memo') }}
            </th>
            <th class="px-[var(--space-cell)] py-[var(--space-inline)]" scope="col">
              <span class="sr-only">{{ t('entries.rowActions') }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="split in row.splits"
            :key="split.id"
            class="border-b border-[color:var(--color-border)]"
            :class="
              isContactTarget(split.targetId)
                ? 'bg-[color:color-mix(in_srgb,var(--color-attention)_10%,transparent)]'
                : undefined
            "
          >
            <td
              class="h-[var(--density-row-tall)] min-w-56 px-[var(--space-cell)] align-middle"
              data-field="target"
            >
              <AccountCombobox
                :id="`split-expense-target-${split.id}`"
                :label="t('splitExpense.targetColumn')"
                :model-value="split.targetId"
                :targets="counterTargets"
                allow-create-contact
                :frecency="frecency"
                @update:model-value="(value) => emit('setTarget', split.id, value)"
              />
            </td>
            <td
              class="h-[var(--density-row-tall)] min-w-40 px-[var(--space-cell)] align-middle"
              data-field="amount"
            >
              <AmountInput
                v-model="split.amount"
                :currency="currency"
                :locale="currentLocale"
                :show-total="false"
                :aria-label="t('entries.amount')"
              />
            </td>
            <td class="h-[var(--density-row-tall)] px-[var(--space-cell)] align-middle">
              <input
                v-model="split.memo"
                type="text"
                maxlength="100"
                :aria-label="t('entries.memo')"
                class="min-h-[var(--density-control)] w-full rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-inline)] text-[length:var(--text-step-3)] text-[color:var(--color-text)]"
              />
            </td>
            <td class="h-[var(--density-row-tall)] px-[var(--space-cell)] align-middle">
              <button
                v-if="row.splits.length > 1"
                type="button"
                :aria-label="t('entries.removeSplit')"
                class="min-h-[var(--density-target-min)] min-w-[var(--density-target-min)] rounded-[var(--radius-control)] px-[var(--space-tight)] text-[color:var(--color-text-muted)] hover:text-[color:var(--color-danger)]"
                @click="emit('removeLine', split.id)"
              >
                ×
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div>
      <button
        type="button"
        class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] px-[var(--space-tight)] text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]"
        @click="emit('addLine')"
      >
        {{ t('splitExpense.addLine') }}
      </button>
    </div>

    <StatusTag v-if="hasSubmitError" tone="danger" role="alert">
      {{ submitErrorDetail ?? t('entries.submitErrorGeneric') }}
    </StatusTag>
    <StatusTag v-else-if="justPosted" tone="ok">
      {{ t('splitExpense.posted') }}
    </StatusTag>

    <div class="flex items-center gap-[var(--space-group)]">
      <ActionButton
        size="lg"
        type="button"
        :disabled="isSubmitting || !canSubmit"
        class="font-medium"
        @click="emit('submit')"
      >
        {{ isSubmitting ? t('splitExpense.posting') : t('splitExpense.submit') }}
      </ActionButton>
      <p
        v-if="!canSubmit && !isSubmitting"
        class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
      >
        {{ t('splitExpense.notBalanced') }}
      </p>
    </div>
  </div>
</template>
